import { useEffect, useMemo, useState } from "react";
import AppShell from "@/components/app/AppShell";
import { api, formatApiError } from "@/lib/api";
import { todayIST } from "@/lib/time";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { ClipboardCheck, Search, ArrowRight, CalendarClock } from "lucide-react";
import { useNavigate } from "react-router-dom";

const CLASSES = Array.from({ length: 12 }, (_, i) => String(i + 1));

export default function NewInspectionPage() {
  const navigate = useNavigate();
  const [meta, setMeta] = useState({ categories: [], sections_by_class: {} });
  const [cls, setCls] = useState("");
  const [sec, setSec] = useState("");
  const [students, setStudents] = useState([]);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [cats, setCats] = useState([]);
  const [remarks, setRemarks] = useState("");
  const [entryDate, setEntryDate] = useState(todayIST());
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/meta").then((r) => setMeta(r.data)); }, []);

  // Sections available for the currently-picked class (11 and 12 have A-D, else A-C)
  const availableSections = useMemo(() => {
    if (!cls) return [];
    return meta.sections_by_class?.[cls] || ["A", "B", "C"];
  }, [cls, meta.sections_by_class]);

  // Reset section if the current one isn't valid for the newly-picked class
  useEffect(() => {
    if (sec && availableSections.length && !availableSections.includes(sec)) {
      setSec("");
    }
  }, [availableSections, sec]);

  useEffect(() => {
    if (!cls || !sec) return setStudents([]);
    api.get("/students", { params: { class_name: cls, section: sec } }).then((r) => setStudents(r.data));
    setSelected(null);
  }, [cls, sec]);

  const filtered = useMemo(() => {
    if (!q.trim()) return students;
    const s = q.trim().toLowerCase();
    return students.filter((st) => st.name.toLowerCase().includes(s));
  }, [q, students]);

  const toggleCat = (v) => setCats((prev) => prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]);

  const save = async () => {
    if (!selected) return toast.error("Pick a student");
    if (cats.length === 0) return toast.error("Choose at least one category");
    if (!entryDate) return toast.error("Pick a date");
    setBusy(true);
    try {
      await api.post("/inspections", { student_id: selected.id, categories: cats, remarks, date: entryDate });
      toast.success(entryDate === todayIST() ? "Inspection recorded" : `Backdated inspection saved for ${entryDate}`);
      setSelected(null); setCats([]); setRemarks("");
      setEntryDate(todayIST());
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  return (
    <AppShell active="/inspections/new">
      <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-carmel-gold mb-2">New Entry</p>
          <h2 className="font-serif text-2xl md:text-4xl tracking-tight">Record Daily Inspection</h2>
          <p className="text-sm text-carmel-navy/60 mt-2 max-w-2xl">
            Pick a class & section, choose the student, tag the applicable violations, add a short remark. Entries stamp today's date automatically.
          </p>
        </div>
        <Button variant="outline" onClick={() => navigate("/records")} data-testid="go-records-btn">
          View records <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Step 1: pick student */}
        <div className="bg-white border border-carmel-navy/10 rounded-lg p-6 shadow-card">
          <div className="flex items-baseline gap-3 mb-4">
            <span className="font-serif text-2xl text-carmel-gold">1.</span>
            <h3 className="font-serif text-2xl tracking-tight">Choose Student</h3>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-carmel-navy/60">Class</Label>
              <Select value={cls} onValueChange={setCls}>
                <SelectTrigger data-testid="insp-class-select" className="mt-1"><SelectValue placeholder="Select class" /></SelectTrigger>
                <SelectContent>{CLASSES.map((c) => <SelectItem key={c} value={c}>Class {c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-carmel-navy/60">Section</Label>
              <Select value={sec} onValueChange={setSec} disabled={!cls}>
                <SelectTrigger data-testid="insp-section-select" className="mt-1"><SelectValue placeholder={cls ? "Select section" : "Pick class first"} /></SelectTrigger>
                <SelectContent>{availableSections.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          <div className="mt-4 bg-carmel-slateBg border border-carmel-navy/10 rounded-md p-3">
            <Label className="flex items-center gap-2 text-xs uppercase tracking-wider text-carmel-navy/70">
              <CalendarClock className="w-3.5 h-3.5" /> Inspection Date
            </Label>
            <Input
              type="date"
              value={entryDate}
              max={todayIST()}
              onChange={(e) => setEntryDate(e.target.value)}
              className="mt-1 bg-white"
              data-testid="insp-date-input"
            />
            <p className="text-[11px] text-carmel-navy/60 mt-1">
              Defaults to today. Pick a past date to record a backdated entry — future dates are blocked.
            </p>
          </div>

          <div className="mt-4 relative">
            <Search className="w-4 h-4 absolute left-3 top-3 text-carmel-navy/40" />
            <Input
              className="pl-9"
              placeholder="Search student..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="insp-search-input"
              disabled={!cls || !sec}
            />
          </div>

          <div className="mt-3 max-h-80 overflow-y-auto border border-carmel-navy/10 rounded-md divide-y divide-carmel-navy/5">
            {(!cls || !sec) ? (
              <p className="text-sm text-carmel-navy/50 p-8 text-center">Pick class & section to load students.</p>
            ) : filtered.length === 0 ? (
              <p className="text-sm text-carmel-navy/50 p-8 text-center">No students in {cls}{sec}. Add them from the Students page.</p>
            ) : filtered.map((s) => (
              <button
                key={s.id}
                onClick={() => setSelected(s)}
                data-testid={`insp-pick-${s.id}`}
                className={`w-full text-left px-4 py-2.5 text-sm transition-colors duration-200 ${selected?.id === s.id ? "bg-carmel-gold/10 text-carmel-goldDark" : "hover:bg-carmel-slateBg"}`}
              >
                <span className="font-medium">{s.name}</span>
                <span className="text-carmel-navy/50 ml-2">· {s.class_name}{s.section}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Step 2: categories + remarks */}
        <div className="bg-white border border-carmel-navy/10 rounded-lg p-6 shadow-card">
          <div className="flex items-baseline gap-3 mb-4">
            <span className="font-serif text-2xl text-carmel-gold">2.</span>
            <h3 className="font-serif text-2xl tracking-tight">Tag Violations</h3>
          </div>

          {selected ? (
            <div className="mb-4 px-4 py-3 bg-carmel-navy text-white rounded-md flex items-center justify-between">
              <div>
                <p className="text-[10px] uppercase tracking-widest text-white/60">Selected</p>
                <p className="font-medium">{selected.name}</p>
              </div>
              <p className="text-sm text-carmel-goldLight">Class {selected.class_name}{selected.section}</p>
            </div>
          ) : (
            <p className="mb-4 text-sm text-carmel-navy/50 italic">Select a student on the left…</p>
          )}

          <div className="space-y-2">
            {meta.categories.map((c) => (
              <label key={c.value} className="flex items-center gap-3 px-3 py-2.5 rounded-md border border-carmel-navy/10 hover:border-carmel-gold/50 cursor-pointer transition-colors duration-200" data-testid={`cat-${c.value}`}>
                <Checkbox checked={cats.includes(c.value)} onCheckedChange={() => toggleCat(c.value)} />
                <span className="text-sm font-medium">{c.label}</span>
              </label>
            ))}
          </div>

          <div className="mt-5">
            <Label className="text-xs uppercase tracking-wider text-carmel-navy/60">Remarks (optional)</Label>
            <Textarea
              rows={3}
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              placeholder="Additional context, e.g. haircut length, tie missing, etc."
              className="mt-1"
              data-testid="insp-remarks-textarea"
            />
          </div>

          <Button
            onClick={save}
            disabled={busy || !selected || cats.length === 0}
            className="mt-6 w-full h-11 bg-carmel-gold hover:bg-carmel-goldDark"
            data-testid="insp-save-button"
          >
            <ClipboardCheck className="w-4 h-4 mr-2" /> Record Inspection
          </Button>
        </div>
      </div>
    </AppShell>
  );
}
